import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useApolloClient } from '@apollo/client';
import {
  Alert,
  Button,
  Input,
  message,
  Skeleton,
  Spin,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  ArrowLeftOutlined,
  DownloadOutlined,
  LeftOutlined,
  LoadingOutlined,
  PlayCircleOutlined,
  RightOutlined,
  ScissorOutlined,
} from '@ant-design/icons';
import {
  addClipNote,
  fetchPpvClipRecords,
  getPresignedUrl,
  isVideoFile,
  isThumbnailFile,
  sortedClipFiles,
} from 'api/aiPpvClips/client';
import { fetchStreamRecordings } from 'api/aiClips/client';
import { AIClipNoteMessage, AIClipRecord } from 'api/aiPpvClips/types';
import { AppContext } from 'context/AppContext';
import { getFileNameWithoutExtension } from 'helpers/utils';
import ENV from 'api/env';
import { AI_PPV_CLIPS } from 'constants/routes';
import styles from '../AIClips/AIClips.module.scss';
import { formatScore, getScoreColor } from '../AIClips/clipScore';
import { recordingsToSources, SourceEntry } from '../AIClips/recordingSources';
import SourcePlaylist from '../AIClips/SourcePlaylist';
import { useClipReviewTheater } from '../AIClips/useClipReviewTheater';

const { Text } = Typography;

interface ClipEntry {
  /** See {@link clipKey} — `name` alone is not unique across recordings. */
  id: string;
  name: string;
  s3Key: string;
  sourceUrl: string;
  thumbUrl: string | null;
  title?: string;
  topic?: string;
  score?: number;
  /** Recording this clip came from — routes notes to the right DynamoDB item. */
  streamRecordId: number | null;
  /** 1-based part number, null when the stream has a single recording. */
  partNumber: number | null;
}

interface RouteParams {
  userSlug: string;
  streamSlug: string;
}

/**
 * Clip filenames repeat across recordings (every job writes clip_01.mp4…), so anything keyed
 * per clip — note threads, resolved URLs, measured durations — is keyed by recording too.
 */
function clipKey(streamRecordId: number | null, clipName: string): string {
  return `${streamRecordId ?? 'legacy'}::${clipName}`;
}

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/** Note threads of one recording, re-keyed by {@link clipKey}. */
function normalizeNotesMap(
  record: Pick<AIClipRecord, 'notes' | 'streamRecordId'>
): Record<string, AIClipNoteMessage[]> {
  if (!record.notes) return {};

  return Object.fromEntries(
    record.notes
      .filter(
        (thread) =>
          thread.clipName.trim().length > 0 && thread.messages.length > 0
      )
      .map((thread) => [
        clipKey(record.streamRecordId ?? null, thread.clipName),
        thread.messages,
      ])
  );
}

/** Lazy-loaded sidebar thumbnail — only loads media when scrolled into view */
const LazyThumb: React.FC<{
  thumbUrl: string | null;
  clipUrl: string | undefined;
  idx: number;
  duration?: number;
}> = React.memo(function LazyThumb({ thumbUrl, clipUrl, idx, duration }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { rootMargin: '100px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div ref={ref} className={styles.clipThumb}>
      {!visible ? (
        <div className={styles.thumbPlaceholder}>
          <ScissorOutlined />
        </div>
      ) : thumbUrl ? (
        <img src={thumbUrl} alt={`Clip ${idx + 1}`} loading="lazy" />
      ) : clipUrl ? (
        <video
          src={`${clipUrl}#t=0.5`}
          preload="metadata"
          muted
          className={styles.clipThumbVideo}
        />
      ) : (
        <div className={styles.thumbPlaceholder}>
          <ScissorOutlined />
        </div>
      )}
      {duration != null && (
        <div className={styles.durationBadge}>{formatDuration(duration)}</div>
      )}
    </div>
  );
});

const AIPpvClipDetail: React.FC = () => {
  const { userSlug, streamSlug } = useParams<RouteParams>();
  const history = useHistory();
  const apolloClient = useApolloClient();
  const { authUser } = useContext(AppContext);
  const decodedUserSlug = decodeURIComponent(userSlug);
  const decodedStreamSlug = decodeURIComponent(streamSlug);

  useClipReviewTheater();

  const [clips, setClips] = useState<ClipEntry[]>([]);
  const [sources, setSources] = useState<SourceEntry[]>([]);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [selectedSourceIdx, setSelectedSourceIdx] = useState(0);
  const [sourceDurations, setSourceDurations] = useState<
    Record<number, number>
  >({});
  const [loading, setLoading] = useState(true);
  const [videoLoading, setVideoLoading] = useState(false);
  const [savingNote, setSavingNote] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notes, setNotes] = useState<Record<string, AIClipNoteMessage[]>>({});
  const [draftNote, setDraftNote] = useState('');
  const [resolvedClipUrls, setResolvedClipUrls] = useState<
    Record<string, string>
  >({});
  const [clipDurations, setClipDurations] = useState<Record<string, number>>(
    {}
  );

  const clipVideoRef = useRef<HTMLVideoElement>(null);
  const noteThreadEndRef = useRef<HTMLDivElement>(null);

  // ── Data loading ──
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      setClips([]);
      setSources([]);
      setSelectedIdx(0);
      setSelectedSourceIdx(0);
      setNotes({});
      setResolvedClipUrls({});
      setClipDurations({});
      setSourceDurations({});
      try {
        const [records, recordings] = await Promise.all([
          fetchPpvClipRecords(apolloClient, decodedUserSlug, decodedStreamSlug),
          fetchStreamRecordings(apolloClient, decodedStreamSlug),
        ]);
        if (!records.length) {
          setError('Record not found in DynamoDB.');
          return;
        }

        // Only label parts when there is more than one recording to tell apart.
        const isMultiPart = records.length > 1;
        const partNumberOf = (idx: number) => (isMultiPart ? idx + 1 : null);

        setNotes(
          Object.assign({}, ...records.map((rec) => normalizeNotesMap(rec)))
        );

        // The playlist comes from what was actually streamed, not from the clip records: a
        // stream clipped before per-recording keys has one record whose video is whichever
        // part overwrote the rest, so it would show a single part out of several.
        const playlist = recordingsToSources(recordings);

        setSources(
          playlist.length
            ? playlist
            : // No recordings visible (deleted stream, or a manual clipper run) — fall back to
              // whatever video the clip records point at so the panel is not empty.
              records
                .map((rec, idx) => ({
                  streamRecordId: rec.streamRecordId ?? null,
                  partNumber: partNumberOf(idx),
                  videoUrl: rec.videoUrl ?? '',
                }))
                .filter((source) => source.videoUrl)
        );

        const resolved: ClipEntry[] = records.flatMap((rec, recIdx) => {
          const sorted = sortedClipFiles(rec.uploadedFileList ?? []);
          const streamRecordId = rec.streamRecordId ?? null;

          return sorted
            .filter((f) => isVideoFile(f.name))
            .map((f) => {
              const stem = getFileNameWithoutExtension(f.name);
              const thumbFile = sorted.find(
                (t) => isThumbnailFile(t.name) && t.name.startsWith(stem)
              );
              return {
                id: clipKey(streamRecordId, f.name),
                name: f.name,
                s3Key: f.s3Key,
                sourceUrl: f.url,
                thumbUrl: thumbFile?.url ?? null,
                title: f.title,
                topic: f.topic,
                score: f.score,
                streamRecordId,
                partNumber: partNumberOf(recIdx),
              };
            });
        });
        setClips(resolved);

        const urlEntries = await Promise.all(
          resolved.map(async (clip) => {
            if (clip.s3Key.startsWith('s3://')) {
              try {
                const url = await getPresignedUrl(apolloClient, clip.s3Key);
                return [clip.id, url] as const;
              } catch {
                return [clip.id, clip.sourceUrl] as const;
              }
            }
            return [clip.id, clip.sourceUrl] as const;
          })
        );
        setResolvedClipUrls(Object.fromEntries(urlEntries));
      } catch (e: any) {
        setError(e?.message || 'Failed to load record.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [decodedUserSlug, decodedStreamSlug, apolloClient]);

  // ── Preload ALL clip videos into browser cache for instant switching ──
  useEffect(() => {
    if (!clips.length || !Object.keys(resolvedClipUrls).length) return;

    const controllers: AbortController[] = [];
    const allUrls = clips
      .map((c) => resolvedClipUrls[c.id])
      .filter(Boolean) as string[];

    // Start with adjacent clips first, then the rest
    const adjacent = [selectedIdx - 1, selectedIdx + 1]
      .filter((i) => i >= 0 && i < clips.length)
      .map((i) => resolvedClipUrls[clips[i].id])
      .filter(Boolean) as string[];

    const rest = allUrls.filter(
      (url) =>
        !adjacent.includes(url) &&
        url !== resolvedClipUrls[clips[selectedIdx]?.id]
    );

    const ordered = [...adjacent, ...rest];

    ordered.forEach((url) => {
      const ctrl = new AbortController();
      controllers.push(ctrl);
      fetch(url, { signal: ctrl.signal, mode: 'cors' }).catch(() => undefined);
    });

    return () => {
      controllers.forEach((c) => c.abort());
    };
  }, [clips, resolvedClipUrls, selectedIdx]);

  // ── Derived state ──
  const selectedClip = clips[selectedIdx] ?? null;
  const selectedClipMessages = selectedClip ? notes[selectedClip.id] || [] : [];
  const selectedClipVideoUrl = selectedClip
    ? resolvedClipUrls[selectedClip.id] || selectedClip.sourceUrl
    : null;
  const selectedSource = sources[selectedSourceIdx] ?? null;

  const selectedClipId = selectedClip?.id;
  const selectedClipRecordId = selectedClip?.streamRecordId ?? null;
  const messagesCount = selectedClipMessages.length;

  useEffect(() => {
    setDraftNote('');
  }, [selectedClipId]);

  // Follow the clip into its own recording, so the reference player always shows the video
  // the selected clip was cut from.
  useEffect(() => {
    if (selectedClipRecordId == null) return;
    const sourceIdx = sources.findIndex(
      (source) => source.streamRecordId === selectedClipRecordId
    );
    if (sourceIdx >= 0) setSelectedSourceIdx(sourceIdx);
  }, [selectedClipRecordId, sources]);

  useEffect(() => {
    requestAnimationFrame(() => {
      noteThreadEndRef.current?.scrollIntoView({
        behavior: 'auto',
        block: 'end',
      });
    });
  }, [selectedClipId, messagesCount]);

  // ── Clip selection (clean — no setTimeout hack) ──
  const handleSelectClip = useCallback(
    (idx: number) => {
      if (idx === selectedIdx) return;
      setVideoLoading(true);
      setSelectedIdx(idx);
    },
    [selectedIdx]
  );

  // ── Keyboard navigation: arrows switch clips, space play/pause ──
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;

      if (e.key === 'ArrowLeft' && selectedIdx > 0) {
        e.preventDefault();
        handleSelectClip(selectedIdx - 1);
      } else if (e.key === 'ArrowRight' && selectedIdx < clips.length - 1) {
        e.preventDefault();
        handleSelectClip(selectedIdx + 1);
      } else if (e.key === ' ' && clipVideoRef.current) {
        e.preventDefault();
        if (clipVideoRef.current.paused) {
          clipVideoRef.current.play();
        } else {
          clipVideoRef.current.pause();
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selectedIdx, clips.length, handleSelectClip]);

  const handleDownloadClip = useCallback(
    async (clip: ClipEntry) => {
      try {
        const url = clip.s3Key?.startsWith('s3://')
          ? resolvedClipUrls[clip.id] ||
            (await getPresignedUrl(apolloClient, clip.s3Key))
          : clip.sourceUrl;

        setResolvedClipUrls((prev) => ({
          ...prev,
          [clip.id]: url,
        }));

        window.open(url, '_blank', 'noopener,noreferrer');
      } catch (e: any) {
        message.error(e?.message || 'Failed to prepare clip download.');
      }
    },
    [apolloClient, resolvedClipUrls]
  );

  const handleSaveNote = useCallback(() => {
    if (!selectedClip) return;

    const save = async () => {
      const trimmedNote = draftNote.trim();
      if (!trimmedNote) return;

      setSavingNote(true);
      try {
        const rawNotes = await addClipNote(
          apolloClient,
          decodedUserSlug,
          decodedStreamSlug,
          selectedClip.name,
          trimmedNote,
          selectedClip.streamRecordId
        );

        // Merge: the response only carries the threads of the recording just written to.
        setNotes((prev) => ({
          ...prev,
          ...normalizeNotesMap({
            notes: rawNotes,
            streamRecordId: selectedClip.streamRecordId,
          }),
        }));
        setDraftNote('');
        message.success('Note added.');
      } catch (e: any) {
        message.error(e?.message || 'Failed to add note.');
      } finally {
        setSavingNote(false);
      }
    };

    save();
  }, [
    apolloClient,
    decodedStreamSlug,
    decodedUserSlug,
    draftNote,
    selectedClip,
  ]);

  // ── Video event handlers ──
  const handleMainVideoMetadata = useCallback(
    (e: React.SyntheticEvent<HTMLVideoElement>) => {
      if (!selectedClip) return;
      const d = e.currentTarget.duration;
      if (d && isFinite(d)) {
        setClipDurations((prev) => ({ ...prev, [selectedClip.id]: d }));
      }
    },
    [selectedClip]
  );

  const handleSourceMetadata = useCallback(
    (e: React.SyntheticEvent<HTMLVideoElement>) => {
      const d = e.currentTarget.duration;
      if (d && isFinite(d)) {
        setSourceDurations((prev) => ({ ...prev, [selectedSourceIdx]: d }));
      }
    },
    [selectedSourceIdx]
  );

  const handleVideoCanPlay = useCallback(() => {
    setVideoLoading(false);
  }, []);

  const handleVideoWaiting = useCallback(() => {
    setVideoLoading(true);
  }, []);

  const handleVideoPlaying = useCallback(() => {
    setVideoLoading(false);
  }, []);

  // ── Loading / error states ──
  if (loading) {
    return (
      <div className={styles.container}>
        <Skeleton active paragraph={{ rows: 10 }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => history.push(AI_PPV_CLIPS)}
          style={{ marginBottom: 16 }}
        >
          Back to list
        </Button>
        <Alert type="error" message={error} showIcon />
      </div>
    );
  }

  return (
    <div className={styles.detailPage}>
      {/* ── Top bar ── */}
      <div className={styles.detailHeader}>
        <Tooltip title="Back to all PPV streams">
          <button
            className={styles.backBtn}
            onClick={() => history.push(AI_PPV_CLIPS)}
            aria-label="Back to all PPV streams"
          >
            <ArrowLeftOutlined />
          </button>
        </Tooltip>
        <Text className={styles.headerTitle}>
          {selectedClip?.topic
            ? `${selectedClip.topic}: ${
                selectedClip?.title || `Clip ${selectedIdx + 1}`
              }`
            : selectedClip?.title || `Clip ${selectedIdx + 1}`}
        </Text>
        <div className={styles.headerTags}>
          {sources.length > 1 && (
            <Tag color="blue">{sources.length} recordings</Tag>
          )}
          <Tag color="default">{clips.length} clips</Tag>
          {selectedClip?.score != null && (
            <Tag color={getScoreColor(selectedClip.score)}>
              Score: {formatScore(selectedClip.score)}
            </Tag>
          )}
        </div>
      </div>

      {/* ── Main layout: fills remaining viewport ── */}
      <div className={styles.mainLayout}>
        {/* Left: Clip selector sidebar */}
        <div className={styles.clipSidebar}>
          <div className={styles.clipSidebarHeader}>
            <Text strong style={{ fontSize: 13 }}>
              Clips
            </Text>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {clips.length} total
            </Text>
          </div>
          {clips.length === 0 ? (
            <Alert
              type="info"
              message="No clips found."
              style={{ margin: 8 }}
            />
          ) : (
            <div className={styles.clipSidebarList}>
              {clips.map((clip, idx) => {
                const clipUrl = resolvedClipUrls[clip.id];
                const duration = clipDurations[clip.id];
                return (
                  <div
                    key={clip.id}
                    role="button"
                    tabIndex={0}
                    className={`${styles.clipCard} ${
                      idx === selectedIdx ? styles.clipCardSelected : ''
                    }`}
                    onClick={() => handleSelectClip(idx)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleSelectClip(idx);
                      }
                    }}
                  >
                    <LazyThumb
                      thumbUrl={clip.thumbUrl}
                      clipUrl={clipUrl}
                      idx={idx}
                      duration={duration}
                    />
                    <div className={styles.clipCardFooter}>
                      <div className={styles.clipCardMeta}>
                        <Text strong style={{ fontSize: 11 }}>
                          Clip {idx + 1}
                        </Text>
                        {clip.partNumber && (
                          <Text type="secondary" style={{ fontSize: 10 }}>
                            Part {clip.partNumber}
                          </Text>
                        )}
                      </div>
                      <Tooltip title="Download">
                        <Button
                          size="small"
                          type="text"
                          icon={<DownloadOutlined style={{ fontSize: 12 }} />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadClip(clip);
                          }}
                        />
                      </Tooltip>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Center: Main Review Stage */}
        <div className={styles.centerColumn}>
          <div className={styles.clipViewer}>
            <div className={styles.clipViewerBody}>
              {selectedIdx > 0 && (
                <button
                  className={styles.navPrev}
                  onClick={() => handleSelectClip(selectedIdx - 1)}
                  aria-label="Previous clip"
                >
                  <LeftOutlined />
                </button>
              )}
              {selectedIdx < clips.length - 1 && (
                <button
                  className={styles.navNext}
                  onClick={() => handleSelectClip(selectedIdx + 1)}
                  aria-label="Next clip"
                >
                  <RightOutlined />
                </button>
              )}

              {/* Video loading overlay */}
              {videoLoading && selectedClipVideoUrl && (
                <div className={styles.videoLoadingOverlay}>
                  <Spin
                    indicator={
                      <LoadingOutlined style={{ fontSize: 32 }} spin />
                    }
                  />
                </div>
              )}

              {selectedClipVideoUrl ? (
                <video
                  key={selectedClipVideoUrl}
                  ref={clipVideoRef}
                  src={selectedClipVideoUrl}
                  controls
                  autoPlay
                  playsInline
                  preload="auto"
                  className={`${styles.videoPlayerPortrait} ${
                    videoLoading ? styles.videoFadeIn : ''
                  }`}
                  aria-label={`AI highlight clip ${selectedIdx + 1}`}
                  onLoadedMetadata={handleMainVideoMetadata}
                  onCanPlay={handleVideoCanPlay}
                  onWaiting={handleVideoWaiting}
                  onPlaying={handleVideoPlaying}
                >
                  <track kind="captions" src="" srcLang="en" label="English" />
                </video>
              ) : (
                <div className={styles.noVideo}>
                  <Text type="secondary">Select a clip to preview</Text>
                </div>
              )}
            </div>
            <div className={styles.clipInfoBar}>
              <span>
                Clip {selectedIdx + 1} / {clips.length}
                {selectedClip?.partNumber
                  ? ` · Part ${selectedClip.partNumber}`
                  : ''}
              </span>
              {clipDurations[selectedClipId ?? ''] != null && (
                <span className={styles.clipDuration}>
                  {formatDuration(clipDurations[selectedClipId ?? ''] ?? 0)}
                </span>
              )}
              <span className={styles.clipKeyHint}>
                Use arrow keys to navigate
              </span>
            </div>
          </div>
        </div>

        {/* Right: Context & Feedback */}
        <div className={styles.rightWorkspace}>
          <div className={styles.referenceSection}>
            <button
              className={styles.sectionHeader}
              onClick={() => {
                const streamUrl = `${ENV.REACT_APP_WEB_PORTAL_URL}/${decodedUserSlug}/watch-stream/video/${decodedStreamSlug}`;
                window.open(streamUrl, '_blank', 'noopener,noreferrer');
              }}
              title="Open original stream"
            >
              <PlayCircleOutlined />
              <span>Original Stream</span>
              <span className={styles.openIcon}>&#8599;</span>
            </button>
            {selectedSource ? (
              <video
                key={selectedSource.videoUrl}
                src={selectedSource.videoUrl}
                controls
                preload="metadata"
                className={styles.referenceVideo}
                aria-label={
                  selectedSource.partNumber
                    ? `Original stream part ${selectedSource.partNumber}`
                    : 'Full stream video'
                }
                onLoadedMetadata={handleSourceMetadata}
              >
                <track kind="captions" src="" srcLang="en" label="English" />
              </video>
            ) : (
              <div className={styles.noVideoSmall}>
                <Text type="secondary">No source video</Text>
              </div>
            )}

            <SourcePlaylist
              sources={sources}
              selectedIdx={selectedSourceIdx}
              onSelect={setSelectedSourceIdx}
              measuredDurations={sourceDurations}
            />
          </div>

          <div className={styles.chatPanel}>
            <div className={styles.chatHeader}>
              <Text strong style={{ fontSize: 13 }}>
                Notes & Feedback
              </Text>
              {selectedClip && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Clip {selectedIdx + 1}
                </Text>
              )}
            </div>
            <div className={styles.chatMessages}>
              {selectedClip ? (
                selectedClipMessages.length ? (
                  selectedClipMessages.map((note) => (
                    <div key={note.id} className={styles.chatBubble}>
                      <div className={styles.chatBubbleHeader}>
                        <Text strong style={{ fontSize: 11 }}>
                          {note.authorId === authUser?.id
                            ? 'You'
                            : note.authorName}
                        </Text>
                      </div>
                      <Text style={{ fontSize: 12 }}>{note.text}</Text>
                    </div>
                  ))
                ) : (
                  <Text type="secondary" className={styles.emptyNotes}>
                    No notes yet for Clip {selectedIdx + 1}
                  </Text>
                )
              ) : (
                <Text type="secondary" className={styles.emptyNotes}>
                  Select a clip to view notes
                </Text>
              )}
              <div ref={noteThreadEndRef} />
            </div>
            {selectedClip && (
              <div className={styles.chatInput}>
                <Input
                  placeholder={`Add a note on Clip ${selectedIdx + 1}…`}
                  value={draftNote}
                  onChange={(e) => setDraftNote(e.target.value)}
                  onPressEnter={(e) => {
                    if (!e.shiftKey) {
                      e.preventDefault();
                      handleSaveNote();
                    }
                  }}
                />
                <Button
                  type="primary"
                  loading={savingNote}
                  disabled={!draftNote.trim()}
                  onClick={handleSaveNote}
                >
                  Send
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIPpvClipDetail;
